import { useEffect, useRef, useState, type TouchEvent } from "react";
import { Link } from "react-router-dom";
import type { Media } from "@/types/media";
import { formatHeat } from "@/utils/media";

interface HeroCarouselProps {
  items: Media[];
}

export default function HeroCarousel({ items }: HeroCarouselProps) {
  const [index, setIndex] = useState(0);
  const [paused, setPaused] = useState(false);
  const touchStartX = useRef(0);

  const count = items.length;

  useEffect(() => {
    if (paused || count <= 1) return;
    const timer = setInterval(() => setIndex((v) => (v + 1) % count), 4500);
    return () => clearInterval(timer);
  }, [paused, count]);

  if (count === 0) return null;

  const current = items[index];
  const go = (dir: number) => setIndex((v) => (v + dir + count) % count);

  const onTouchStart = (e: TouchEvent) => {
    touchStartX.current = e.touches[0].clientX;
  };
  const onTouchEnd = (e: TouchEvent) => {
    const delta = e.changedTouches[0].clientX - touchStartX.current;
    if (Math.abs(delta) > 50) go(delta < 0 ? 1 : -1);
  };

  return (
    <section
      className="relative overflow-hidden rounded-xl bg-primary-900"
      onMouseEnter={() => setPaused(true)}
      onMouseLeave={() => setPaused(false)}
      onTouchStart={onTouchStart}
      onTouchEnd={onTouchEnd}
      aria-label="焦点影视轮播"
    >
      <div className="relative grid grid-cols-1 md:grid-cols-5">
        <div className="relative z-10 flex flex-col justify-center gap-3 p-6 md:col-span-3 md:p-10">
          <span className="flex w-fit items-center gap-1.5 rounded-full bg-accent-500 px-3 py-1 text-xs font-semibold text-primary-950">
            <i className="ri-fire-fill" />
            本周焦点 · {current.type === "movie" ? "电影" : "电视剧"}
          </span>
          <h2 className="font-heading text-2xl font-bold leading-tight text-background-50 md:text-4xl">
            {current.title}
          </h2>
          <p className="flex flex-wrap items-center gap-x-3 gap-y-1 text-sm text-background-200/90">
            <span className="flex items-center gap-1 font-semibold text-accent-400">
              <i className="ri-star-fill" />
              {current.rating.toFixed(1)}
            </span>
            <span>{current.year}</span>
            <span className="text-background-300/60">|</span>
            <span>{current.region}</span>
            <span className="text-background-300/60">|</span>
            <span>{current.genres.join(" · ")}</span>
          </p>
          <p className="line-clamp-3 max-w-xl text-sm leading-relaxed text-background-200/75">
            {current.synopsis}
          </p>
          <div className="mt-2 flex flex-wrap items-center gap-3">
            <Link
              to={`/detail/${current.id}`}
              className="flex items-center gap-1.5 whitespace-nowrap rounded-md bg-accent-500 px-5 py-2.5 text-sm font-semibold text-primary-950 transition-colors hover:bg-accent-400"
            >
              <i className="ri-play-fill text-base" />
              立即观看
            </Link>
            <Link
              to={`/detail/${current.id}`}
              className="flex items-center gap-1.5 whitespace-nowrap rounded-md border border-background-200/40 px-5 py-2.5 text-sm font-medium text-background-50 transition-colors hover:bg-background-50/10"
            >
              <i className="ri-information-line text-base" />
              查看详情
            </Link>
          </div>
        </div>

        <div className="relative md:col-span-2">
          <div className="absolute inset-0 hidden md:block bg-gradient-to-r from-primary-900 via-primary-900/60 to-transparent" />
          <div className="relative h-52 w-full md:h-full">
            <img
              src={current.poster}
              alt={`${current.title}海报`}
              title={`${current.title}焦点推荐`}
              className="h-full w-full object-top"
            />
          </div>
        </div>
      </div>

      <button
        type="button"
        aria-label="上一张"
        onClick={() => go(-1)}
        className="absolute left-3 top-1/2 z-20 flex h-9 w-9 -translate-y-1/2 items-center justify-center rounded-full bg-primary-950/50 text-background-50 transition-colors hover:bg-primary-950/80"
      >
        <i className="ri-arrow-left-s-line text-xl" />
      </button>
      <button
        type="button"
        aria-label="下一张"
        onClick={() => go(1)}
        className="absolute right-3 top-1/2 z-20 flex h-9 w-9 -translate-y-1/2 items-center justify-center rounded-full bg-primary-950/50 text-background-50 transition-colors hover:bg-primary-950/80"
      >
        <i className="ri-arrow-right-s-line text-xl" />
      </button>

      <div className="absolute bottom-4 left-1/2 z-20 flex -translate-x-1/2 items-center gap-2">
        {items.map((item, i) => (
          <button
            key={item.id}
            type="button"
            aria-label={`切换到第 ${i + 1} 张`}
            onClick={() => setIndex(i)}
            className={`h-2 rounded-full transition-all ${
              i === index ? "w-6 bg-accent-500" : "w-2 bg-background-50/40"
            }`}
          />
        ))}
      </div>
    </section>
  );
}